import React from 'react'
import { SubtotalProps } from '../../lib/type'
import { useTranslation } from 'next-i18next'
import { formatPrice } from '@corratech/pylot-price'
import { useRouter } from 'next/router'
import cn from 'classnames'

import {
    BundleItemOption,
    Maybe,
    ProductInterface
} from '@pylot-data/fwrdschema'
import ds from '../DeviceSelection/DeviceTile.module.scss'

type TSubtotalProps = SubtotalProps & {
    podsPrice?: number
}

export const Subtotal = ({
    product,
    selectedOptions,
    addOns,
    podsPrice = 0
}: TSubtotalProps): JSX.Element => {
    const { t } = useTranslation(['common', 'plp'])
    const { locale } = useRouter()
    const options: {
        [key: string]: { sku: string; ooka_product_option: string }
    } = {}
    const selectedProducts =
        product?.items?.flatMap((item) => {
            return item?.options?.filter((option) => {
                const uid = option?.uid
                const sku = option?.product?.sku
                const ookaOption = item?.ooka_product_option
                if (
                    uid &&
                    selectedOptions?.includes(uid) &&
                    sku &&
                    ookaOption
                ) {
                    options[sku] = {
                        sku,
                        ooka_product_option: ookaOption
                    }
                    return true
                }
                return false
            })
        }) ?? []
    const adonProducts: Maybe<ProductInterface>[] | undefined =
        product?.upsell_products?.filter((individualProduct) => {
            const sku = individualProduct?.sku
            return sku ? addOns?.[sku]?.qty : false
        }) ?? []
    const devicePrice = selectedProducts?.reduce(
        (accumulate: number, item?: Maybe<BundleItemOption>) => {
            const price =
                item?.product?.price_range?.minimum_price?.final_price?.value ??
                0
            return price + accumulate
        },
        0
    )
    const deviceRegularPrice = selectedProducts?.reduce(
        (accumulate: number, item?: Maybe<BundleItemOption>) => {
            const price =
                item?.product?.price_range?.minimum_price?.regular_price
                    ?.value ?? 0
            return price + accumulate
        },
        0
    )
    const adonPrice = adonProducts?.reduce(
        (accumulate: number, item?: Maybe<ProductInterface>) => {
            const sku = item?.sku
            const qty = sku && addOns?.[sku]?.qty ? addOns[sku].qty : 1
            const price =
                item?.price_range?.minimum_price?.final_price?.value ?? 0
            return price * qty + accumulate
        },
        0
    )
    const adonRegularPrice = adonProducts?.reduce(
        (accumulate: number, item?: Maybe<ProductInterface>) => {
            const sku = item?.sku
            const qty = sku && addOns?.[sku]?.qty ? addOns[sku].qty : 1
            const price =
                item?.price_range?.minimum_price?.regular_price?.value ?? 0
            return price * qty + accumulate
        },
        0
    )

    const finalTotal = devicePrice + adonPrice + podsPrice
    const regularTotal = deviceRegularPrice + adonRegularPrice + podsPrice
    const currency = product?.price_range?.minimum_price?.final_price?.currency
    const showDiscount = regularTotal > finalTotal

    const specialPrice = formatPrice(
        { value: finalTotal, currency },
        locale ?? 'en'
    )
    const originalPrice = showDiscount
        ? formatPrice({ value: regularTotal, currency }, locale ?? 'en')
        : null

    return (
        <div className={cn(ds['price'], 'subtotal-price')}>
            <h5 className={ds['price-label']}>
                {t('Subtotal: ')}
                <span
                    className={cn(
                        ds['price-ammount'],
                        showDiscount && ds['discounted-price']
                    )}
                >
                    {specialPrice}
                </span>
                {originalPrice && (
                    <span
                        className={cn(ds['price-ammount'], ds['regular-price'])}
                    >
                        {originalPrice}
                    </span>
                )}
            </h5>
        </div>
    )
}
